import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AppButton, AppInput, Divider, Typography } from "@libs/zapi-ui/custom";
import { TrashSimpleIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useRef, useState, useEffect } from "react";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { toast } from "sonner";
import { useUpdateUserProfile } from "../services/useUpdateUserProfile";
import { useDeleteUserPhoto } from "../services/useDeleteUserPhoto";

export function UserSettings() {
  const { firstName, lastName, profileImageUrl, refetch } = useMeStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: updateUserProfile, isPending: isSaving } = useUpdateUserProfile();
  const { mutate: deleteUserPhoto, isPending: isDeleting } = useDeleteUserPhoto();

  const [formData, setFormData] = useState({ firstName: "", lastName: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({ firstName: firstName ?? "", lastName: lastName ?? "" });
  }, [firstName, lastName]);

  const isChanged = formData.firstName !== firstName || formData.lastName !== lastName || selectedFile !== null;

  function fileClickHandler() {
    fileInputRef.current?.click();
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, PNG and WebP files are allowed.");
      return "Invalid file type. Only JPG, PNG and WebP files are allowed.";
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must not exceed 5MB.");
      return "File size must not exceed 5MB.";
    }

    return null;
  }

  async function fileChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleRemovePhoto = async () => {
    setUploadError(null);

    deleteUserPhoto(undefined, {
      onSuccess: async () => {
        toast.success("Profile photo removed successfully");
        if (refetch) await refetch();
      },
      onError: (error) => {
        console.error("Failed to remove profile photo:", error);
        const err = error as { response?: { data?: { message?: string } } };
        setUploadError(err.response?.data?.message || "Failed to remove photo. Please try again.");
        toast.error("Failed to remove photo. Please try again.");
      }
    });
  };

  const handleSaveChanges = async () => {
    setUploadError(null);

    updateUserProfile(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        file: selectedFile ?? undefined
      },
      {
        onSuccess: async () => {
          toast.success("Profile updated successfully");
          if (refetch) await refetch();

          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: (error) => {
          toast.error("Failed to update profile. Please try again.");
          console.error("Failed to update profile:", error);
        }
      }
    );
  };

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <Typography variant="heading6" color="zinc900">
        Profile
      </Typography>

      <div className="flex flex-col gap-8">
        <div className="flex gap-6">
          <button
            className={cn(
              "border-none outline-none cursor-pointer flex items-center justify-center",
              "w-[88px] h-[88px] rounded-full bg-innoveraOrange-400 overflow-hidden"
            )}
            onClick={fileClickHandler}
          >
            {(previewUrl || profileImageUrl) && (
              <img src={previewUrl || profileImageUrl || ""} alt="user img" className="w-full h-full object-cover" />
            )}
            {!previewUrl && !profileImageUrl && (
              <Typography className="text-[40px] text-white">{firstName?.[0]}</Typography>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={fileChangeHandler}
          />

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <AppButton
                appearance="contained"
                color="zinc100"
                startAdornment={<UploadSimpleIcon />}
                onClick={fileClickHandler}
                disabled={isSaving || isDeleting}
              >
                Upload Photo
              </AppButton>
              {profileImageUrl && (
                <AppButton
                  appearance="text"
                  startAdornment={<TrashSimpleIcon />}
                  onClick={handleRemovePhoto}
                  disabled={isSaving}
                  isLoading={isDeleting}
                  loadingText="Removing..."
                >
                  Remove Photo
                </AppButton>
              )}
            </div>
            <div>
              <Typography variant="body3" color="zinc500">
                For best results, upload a square image (400x400 px)
              </Typography>
              <Typography variant="body3" color="zinc500">
                JPG, PNG or WebP format, up to 5MB
              </Typography>
              {uploadError && (
                <Typography variant="body3" className="text-red-500 mt-1">
                  {uploadError}
                </Typography>
              )}
            </div>
          </div>
        </div>

        <Divider className="!bg-innoveraZinc-100" />

        <div className="flex gap-1">
          <AppInput
            label="First Name"
            className="w-[250px]"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            disabled={isSaving || isDeleting}
          />
          <AppInput
            label="Last Name"
            className="w-[250px]"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            disabled={isSaving || isDeleting}
          />
        </div>

        <AppButton
          className="w-fit"
          onClick={handleSaveChanges}
          disabled={isSaving || isDeleting || !isChanged}
          isLoading={isSaving}
          loadingText="Saving..."
        >
          Save changes
        </AppButton>
      </div>
    </div>
  );
}
